import axios from "axios";
import moment from "moment";
import { connect, useSelector } from "react-redux";
import { Line } from "react-chartjs-2";
import { useState, useEffect, memo } from "react";
import DebatesBox from "../../DebatesBox";
import OrganizationSelector from "../../OrganizationSelector";
import NetworksSelector from "../../NetworksSelector";
import IntervalSelector from "../../IntervalSelector";
import { ChevronIcon } from "../../../icons";
import {
  API_URL,
  FORMAT_DATE_API,
  REPORTS_API_URL,
} from "../../../constants/Globals";
import { Link } from "react-router-dom";
import { numFormatter } from "../../../helpers/Number";

const ArrowIcon = ({ className = "" }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.99778 11.44H6.44222V2.77334L10.4144 6.74556L11.44 5.72L5.72 2.86102e-06L0 5.72L1.02556 6.74556L4.99778 2.77334V11.44Z"
      fill="#2AA974"
    />
  </svg>
);
const percentSlugs = ["contactConversionRate", "engagementRate", "voteRate", "ctaButtonClickRate"]
const InfoStatsBox = memo(
  ({
    title,
    slug,
    intervalDates,
    selectedNetworks,
    groupId = "",
    isMoney = false,
    isPercent = false,
  }) => {
    const [value, setValue] = useState(0);

    useEffect(() => {
      if (intervalDates.start < intervalDates.end) {
        axios
          .get(`${REPORTS_API_URL}/stats/${slug}`, {
            params: {
              groupId: groupId,
              fromDate: intervalDates.start.format(FORMAT_DATE_API),
              toDate: intervalDates.end.format(FORMAT_DATE_API),
              statComparison: 1,
              clientIds: selectedNetworks.join(","),
            },
          })
          .then(({ data }) => {
            if (percentSlugs.includes(slug)) {
              data.data.previousStat.totalCount = parseFloat(data.data.previousStat.totalCount) * 100;
              data.data.statDifference.totalCount = parseFloat(data.data.statDifference.totalCount) * 100;
              data.data.totalCount =  parseFloat(data.data.totalCount) * 100;
            }
            setValue(data.data);
          });
      }
    }, [intervalDates, slug, groupId, selectedNetworks]);

    const changePercent =
      (parseFloat(value?.statDifference?.totalCount) * 100) /
      parseFloat(value?.previousStat?.totalCount);
    const valueToDisplay = value !== 0 ? numFormatter(value?.totalCount) : 0;

    return (
      <div>
        <div className="info-stat-title">{title}</div>
        <div className="info-stat-value">
          {isMoney ? "$" : ""}
          {valueToDisplay}
          {isPercent ? "%" : ""}
          {parseFloat(value?.statDifference?.totalCount) === 0 ||
          parseFloat(value?.previousStat?.totalCount) === 0 ||
          isNaN(changePercent) ? (
            <span className="no-change"></span>
          ) : (
            <>
              <ArrowIcon
                className={
                  parseFloat(value?.statDifference?.totalCount) < 0
                    ? "change-down"
                    : "change-up"
                }
              />
              <span>{Math.abs(changePercent).toFixed(1)}%</span>
            </>
          )}
        </div>
      </div>
    );
  }
);

function ReportsOverview({ allNetworks = [], groups = [] }) {
  const loggedInUserData = useSelector((state) => state.login.user);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedNetworks, setSelectedNetworks] = useState([]);
  const [groupNetworks, setGroupNetworks] = useState([]);
  const [intervalDates, setIntervalDates] = useState({
    start: moment().subtract(30, "days"),
    end: moment(),
  });
  const [lastUpdatedDate, setLastUpdatedDate] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [groupDDLength, setGroupDDLength] = useState(0);
  const infoStatsBoxes = [
    { title: "Poll Impressions", slug: "pollImpressions" },
    { title: "Votes", slug: "votes" },
    { title: "Engagements", slug: "engagements" },
    { title: "Ad Impressions", slug: "adImpressions" },
    { title: "Revenue", slug: "revenue" },
    { title: "CPM", slug: "cpm" },
    { title: "New Contacts", slug: "contacts" },
    { title: "Audience", slug: "audience" },
    { title: "Contact Conversion Rate", slug: "contactConversionRate" },
    { title: "Viewed Poll Impressions", slug: "viewedPollImpressions" },
    { title: "CTA Button Clicks", slug: "ctaButtonClicks" },
    { title: "Engagement Rate", slug: "engagementRate" },
    { title: "Vote Rate", slug: "voteRate" },
    { title: "CTA Button Click Rate", slug: "ctaButtonClickRate" },
  ];

  const fetchLastUpdatedRecords = async () => {
    const response = await axios.get(
      REPORTS_API_URL + "/reports/last-updated/stats-client-embed-domain-summary"
    );
    if (response.data.status === 1) {
      setLastUpdatedDate(moment().diff(moment(response.data.data), "hours"));
    }
  };

  useEffect(() => {
    fetchLastUpdatedRecords();
  }, []);
  useEffect(() => {
    if (selectedGroup) {
      axios
        .get(
          `${API_URL}/admin/groups/getGroupNetworks?groupId=${selectedGroup}&pageNumber=1&perPage=100&order=asc&orderBy=name`
        )
        .then(({ data }) => {
          setGroupNetworks(data.data);
        });
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (intervalDates.start < intervalDates.end) {
      axios
        .get(`${REPORTS_API_URL}/reports/viewed-polls`, {
          params: {
            groupId: selectedGroup,
            fromDate: intervalDates.start.format(FORMAT_DATE_API),
            toDate: intervalDates.end.format(FORMAT_DATE_API),
            clientIds: selectedNetworks.join(","),
          },
        })
        .then(({ data }) => setChartData(data.data || []));
    }
  }, [intervalDates, selectedGroup, selectedNetworks]);

  useEffect(() => {
    const isGroupAdmin = loggedInUserData.roles.find((role) => role.name === "GROUPADMIN");
    if (isGroupAdmin) {
      setGroupDDLength(1);
    }
  }, []);

  return (
    <>
      <div className="page-headline is-flex">
        <div>
          <strong>Reports</strong>&nbsp;|&nbsp;Overview
        </div>
        <IntervalSelector
          startDate={intervalDates.start}
          endDate={intervalDates.end}
          onChange={(startDate, endDate) =>
            setIntervalDates({
              start: startDate,
              end: endDate,
            })
          }
        />
      </div>

      <div className="page-selectors">
        {groups?.length > groupDDLength && (
          <OrganizationSelector
            value={selectedGroup}
            onChange={(id) => {
              setSelectedGroup(id);
              setSelectedNetworks([]);
              setGroupNetworks([]);
            }}
          />
        )}
        <NetworksSelector
          multiple={true}
          value={selectedNetworks}
          defaultText="All Networks"
          networks={selectedGroup ? groupNetworks : allNetworks}
          onChange={(ids) => setSelectedNetworks(ids)}
        />
      </div>

      <div className="info-box">
        <div className="info-box-header">Last 30 days</div>
        <div className="info-box-content">
          <div className="info-stats">
            {infoStatsBoxes.map(({ title, slug }) => (
              <InfoStatsBox
                isMoney={["revenue", "cpm"].includes(slug)}
                intervalDates={intervalDates}
                isPercent={percentSlugs.includes(slug)}
                selectedNetworks={selectedNetworks}
                groupId={selectedGroup}
                title={title}
                slug={slug}
                key={slug}
              />
            ))}
          </div>

          {chartData.length ? (
            <div className="chart-box">
              <Line
                height={250}
                options={{
                  maintainAspectRatio: false,
                  aspectRatio: 1,
                  responsive: true,
                  plugins: { legend: { display: false } },
                  scales: {
                    y: {
                      min: 0,
                      max: Math.max(
                        ...[10].concat(
                          (chartData || []).map(({ totalViews }) =>
                            parseInt(totalViews)
                          )
                        )
                      ),
                      stepSize: 1,
                      ticks: {
                        callback: (label, index, labels) => label,
                      },
                    },
                  },
                }}
                data={{
                  labels: (chartData || []).map(({ createdAt }) =>
                    moment(createdAt).format("MMMM D, YYYY")
                  ),
                  datasets: [
                    {
                      label: "",
                      data: (chartData || []).map(({ totalViews }) =>
                        parseInt(totalViews)
                      ),
                      borderColor: "rgb(53, 162, 235)",
                      backgroundColor: "rgba(53, 162, 235, 0.5)",
                    },
                  ],
                }}
              />
            </div>
          ) : null}
        </div>
        <div className="info-box-footer">
          <div>Data updated {lastUpdatedDate} hours ago</div>
          <div>
            <Link to="/reports/performance">
              <span className="text-button inherit-font icon-right">
                <span>Performance Report</span>
                <ChevronIcon />
              </span>
            </Link>
          </div>
        </div>
      </div>

      <DebatesBox
        selectedGroup={selectedGroup}
        selectedNetworks={selectedNetworks.length ? selectedNetworks : allNetworks ? allNetworks.map((n) => n.id) : []}
        title={<h3>Top Polls</h3>}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  allNetworks: state.networks.networksList,
  groups: state.networks.groupsList,
});

export default connect(mapStateToProps, {})(ReportsOverview);
